// RIBBON control renderer — descriptor → house control (R0, 2026-07-20).
//
// Rowsets describe controls as DATA; this component resolves a descriptor
// against the two flat bags the mount provides:
//   state[descriptor.read]        — mirror value (useSelectionProps vocabulary)
//   handlers[descriptor.handler]  — action fn for the HANDLER KEY (string)
// R0's lab feeds mocks; R1 swaps in the real mirror + resolution table without
// touching a single rowset. Rendering reuses the house Tb primitives
// (primitives/toolbarPrimitives — presentational leaf module, not forked).
//
// Control kinds and their invocation shape (THE contract R1 wires to):
//   button     handlers[h]()                    one-shot action
//   toggle     handlers[h]()                    mount derives the flip from state
//   color      handlers[h](hexString)
//   number     handlers[h](number)              min/max/step honored
//   stepper    handlers[h](number)              − / field / + ; step per press
//   select     handlers[h](optionValue)
//   segmented  handlers[h](optionValue)
//   chips      handlers[h](optionValue)         icon chip row (curve, lists…)
//   slider     handlers[h](number)              commit hint rides d.commit
//   font       handlers[h](family)              the house FontPicker (R2)
//   badge      —                                read-only status pill (R2)
//   ('swatches' and 'gradient' RETIRED by the PaintPicker standard,
//    2026-07-20 — the page background is a colorflyout now; presets fold
//    into the Solid palette, the two-color popover into the Gradient pane)
//   menu       nested descriptors in a popover (rendered by RibbonGroup's menu)
//   scope      the ScopeChip shell (special-cased by RibbonGroup)
//   placeholder inert stub — shows state[d.read] when read is set (live
//              readouts), else d.text
//
// R2.5 FLYOUT KINDS (owner redesign — every multi-option control is ONE
// button + a corner-chip flyout; core/RibbonFlyout.jsx is the primitive):
//   sizefield    editable number field; focus/click opens a slider flyout
//                (typing → handlers[h](n) 'soon'; slider → d.slideHandler
//                'defer' ticks + history.commitNow on release)
//   flyoutchips  one-of picker: face shows the CURRENT option (icon/text),
//                flyout lists options (current ✓); d.faceHandler makes the
//                face a FAST PATH (Bold click toggles; only the chip opens)
//   colorflyout  PaintChip button → opens the house paint MODAL directly
//                (owner 2026-07-21 — no flyout/ColorFlyoutPanel; the modal
//                carries the PaintPicker standard). d.handler = the solid pick
//                key, d.recipeHandler = the recipe key, d.recipeRead = FillValue
//                mirror, d.solidOnlyWhen = Solid-pane-only gate for kinds whose
//                recipes would lie, d.allow/d.linearOnly = narrower engine
//                truth (page bg), d.paletteFrom = mirror-fed Solid palette;
//                title from state.colorTitle (kind-true). inMenu still degrades
//                to a flat TbColorField chip (which also opens the modal).
//   flyoutsliders one button → stacked sliders (d.sliders[]), defer ticks +
//                one commit on release each
//
// R3 Row-2 section kinds (owner rework — see rowsetSchema.mjs):
//   outlineflyout ONE quiet button → swatches + custom color + width slider
//                + "No outline" (colorHandler / handler / clearHandler)
//   bevelflyout  SPLIT: face = faceHandler toggle; panel = depth/width
//                sliders + Rounded|Chiseled + poster-sun check (fixed
//                depth.setBevel* keys — the drawer's exact verbs)
//   threedflyout SPLIT: face = faceHandler toggle; panel = depth slider +
//                finish chips + sticker plate block (fixed depth.set3D* /
//                depth.setPlate* keys)
//   flyoutmenu   one button → an action list (items fire their own keys,
//                panel closes on pick; dividers + showWhen rows)
//
// PER-CONTROL DISABLED (R3): d.disabledWhen (mirror key) renders the control
// inside a <fieldset disabled> of its own — native cascade, siblings stay
// live (the group-level fieldset still handles whole-group policy) — with
// d.disabledTitle as the plain-words tooltip.
// Inside a collapsed chip menu (inMenu) flyout kinds degrade to their flat R2
// renderings (chips row / plain field / plain color / stacked sliders /
// flat action list) — no popover-in-popover.
// Every descriptor also carries commit: 'now'|'soon'|'defer' — R0 renders it
// as data (data-commit) so probes can read it; R1 maps it onto apply()'s modes.
import React, { useEffect, useState } from 'react';
import {
  TbButton, TbColorField, TbNumberField, TbSlider, TbSegmented,
} from '../../primitives/toolbarPrimitives';
// The house paint standard (owner 2026-07-20): PaintChip faces + PaintField
// chips. Every color surface now opens the ONE house paint modal (owner
// 2026-07-21) via openPaintModal — no picker-in-flyout/popover anywhere.
import { PaintChip, PaintField, HOUSE_SWATCHES } from '../../primitives/PaintPicker';
import { openPaintModal } from '../../paintModalController';
import FontPicker from '../../toolbar/FontPicker';
import { getIcon } from './iconRegistry';
import RibbonFlyout, { FlyoutOption, FlyoutFooter } from './RibbonFlyout';
// The Bevel + 3D effect-tile faces — the SAME styled-`Aa` art the drawer's
// depth block shows (one source of truth; owner §depth). The face replaces the
// old "Bevel"/"3D" text label; the corner chip + tuning panel are unchanged.
import DepthTileFace from '../../inspector/text/depthTileFaces';
// The eight text-effect faces (Shadow/Lift/Glow/Echo · Outline/Hollow/Trace/
// Sticker) render the SAME styled-`Aa` idiom straight from effectDefs — one
// source with the drawer's Style-tab tiles (owner, 2026-07-20: "add the other
// effects … as icons in the ribbon too").
import EffectTileFace from './EffectTileFace';
import { EFFECT_DEFS, effectDefaults, luminance } from '../../inspector/text/effectDefs';

/** Declarative active-state: a string = truthy mirror key; an object supports
 *  {read, eq} / {read, neq} / {read, gt} / {read, truthy}. */
export function isActive(activeWhen, state) {
  if (!activeWhen) return false;
  if (typeof activeWhen === 'string') return !!state[activeWhen];
  const v = state[activeWhen.read];
  if ('eq' in activeWhen) return v === activeWhen.eq;
  if ('neq' in activeWhen) return v !== activeWhen.neq;
  if ('gt' in activeWhen) return Number(v) > activeWhen.gt;
  return !!v;
}

const fire = (handlers, key) => (...args) => {
  const fn = handlers?.[key];
  if (typeof fn === 'function') fn(...args);
  else if (import.meta.env.DEV) console.warn(`[ribbon] unresolved handler key: ${key}`);
};

/** kind 'sizefield' — the font-size control (owner directive §2): an
 *  editable number field ONLY (no ± steppers); focusing or clicking it opens
 *  a slider flyout. Typing applies through the field's handler ('soon');
 *  the slider previews via slideHandler ('defer') and lands ONE history
 *  entry on release (history.commitNow). The field keeps working while the
 *  panel is up — the panel never steals focus. */
function SizeFieldControl({ d, state, handlers, on }) {
  const [draft, setDraft] = useState(null);
  const live = state[d.read];
  const cur = Number.isFinite(live) ? live : (d.fallback ?? 0);
  // The flyout slider moves the LIVE value while the field may still hold a
  // focus-time draft — drop the draft whenever the mirror moves so the field
  // always tells the truth (typing re-drafts on the next keystroke; the
  // digits the user just typed re-arrive via the mirror anyway).
  useEffect(() => { setDraft(null); }, [cur]);
  const slide = (v) => {
    const fn = handlers?.[d.slideHandler || d.handler];
    if (typeof fn === 'function') fn(v);
  };
  return (
    <RibbonFlyout
      flyId={d.id}
      label={d.label}
      panelClassName="rbn-size-panel"
      faceAs={({ setOpen, faceRef }) => (
        <input
          ref={faceRef}
          type="number"
          className="cce-tb-number rbn-size-input"
          value={draft !== null ? draft : Math.round(cur)}
          min={d.min}
          max={d.max}
          step={d.step ?? 1}
          aria-label={d.label}
          title={`${d.label} (pt) — click for the size slider`}
          onFocus={(e) => { setDraft(e.target.value); setOpen(true); }}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setDraft(e.target.value);
            const v = parseFloat(e.target.value);
            if (!Number.isNaN(v)) on(Math.min(d.max ?? Infinity, Math.max(d.min ?? -Infinity, v)));
          }}
          onBlur={() => setDraft(null)}
        />
      )}
      panel={() => (
        <div className="rbn-size-slider" data-size-slider="true">
          <TbSlider
            label={`${Math.round(cur)} pt`}
            value={Math.round(cur)}
            min={d.sliderMin ?? d.min ?? 6}
            max={d.sliderMax ?? d.max ?? 600}
            step={1}
            onChange={(v) => slide(v)}
            onCommit={() => handlers?.['history.commitNow']?.()}
          />
        </div>
      )}
    />
  );
}

/** kind 'outlineflyout' — ONE quiet button for the whole border story
 *  (owner §1: the two color buttons + width field were the confusion).
 *  The paint half is the house PaintPicker (SOLID pane only — setBorder
 *  speaks flat color strings; a gradient/texture tab here would lie about
 *  the engine): any pick fires colorHandler (turning a zero-width border on
 *  at 2 — the mount's affordance), the slider previews width ('defer') and
 *  lands ONE entry on release, "No outline" clears. Adjustment surface —
 *  the panel stays open. */
function OutlineFlyControl({ d, state, handlers, Icon, drawerSection, onOpenDrawer }) {
  const width = Number.isFinite(state[d.read]) ? state[d.read] : (d.fallback ?? 0);
  const color = typeof state[d.colorRead] === 'string' ? state[d.colorRead] : '#000000';
  return (
    <RibbonFlyout
      flyId={d.id}
      label={d.label}
      active={isActive(d.activeWhen, state)}
      face={Icon ? <Icon /> : <span className="rbn-btn-text">{d.text ?? d.label}</span>}
      panelClassName="rbn-outline-panel"
      panel={({ close }) => (
        <>
          {/* Color area (owner 2026-07-21): a compact swatch row for quick
              picks + the current-color chip that opens the ONE house paint
              modal (solid-only — setBorder speaks flat color strings). The
              width slider + "No outline" keep this flyout. */}
          <div className="rbn-outline-swatches" role="group" aria-label="Outline color">
            {HOUSE_SWATCHES.map((c) => (
              <button
                key={c}
                type="button"
                className={`rbn-outline-swatch${color.toLowerCase() === c.toLowerCase() ? ' active' : ''}`}
                style={{ background: c }}
                title={c}
                aria-label={`Outline color ${c}`}
                data-swatch={c}
                onClick={() => fire(handlers, d.colorHandler)(c, 'now')}
              />
            ))}
            <PaintChip
              value={color}
              className="rbn-swatch-face rbn-outline-chip"
              title="Outline color — more colors"
              ariaHaspopup="dialog"
              onClick={() => {
                openPaintModal({
                  fieldId: 'outlineColor',
                  title: 'Outline color',
                  value: color,
                  allow: ['solid'],
                  density: 'compact',
                  onChange: (next, { live } = {}) => {
                    if (typeof next === 'string') fire(handlers, d.colorHandler)(next, live ? 'soon' : 'now');
                  },
                  onCommit: () => handlers?.['history.commitNow']?.(),
                });
                close();
              }}
            />
          </div>
          <div className="rbn-fly-slider-row" data-slider-id="outlineWidth">
            <span className="rbn-fly-slider-name">Width</span>
            <TbSlider
              label={String(width)}
              value={width}
              min={d.min ?? 0}
              max={d.max ?? 100}
              step={d.step ?? 1}
              onChange={(v) => fire(handlers, d.handler)(v)}
              onCommit={() => handlers?.['history.commitNow']?.()}
            />
          </div>
          <button
            type="button"
            className="rbn-menu-action rbn-outline-none"
            onClick={() => fire(handlers, d.clearHandler)()}
          >
            No outline
          </button>
          <FlyoutFooter drawerSection={drawerSection} onOpenDrawer={onOpenDrawer} close={close} />
        </>
      )}
    />
  );
}

/** Small labeled checkbox row shared by the depth flyouts (the drawer's
 *  cce-poster-check pattern, popover-sized). */
function FlyCheckRow({ label, checked, onToggle, klass = '' }) {
  return (
    <label className={`rbn-fly-check-row${klass ? ` ${klass}` : ''}`}>
      <input type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

// The drawer's exact slider ranges (TextCustomizePanel depth block) — the
// flyout is the same dial in a popover, so the ranges must agree.
const BEVEL_SHAPE_OPTIONS = [
  { value: 'smooth', label: 'Rounded' },
  { value: 'chisel', label: 'Chiseled' },
];
const FINISH_OPTIONS = [
  { value: 'matte', label: 'Matte' },
  { value: 'satin', label: 'Satin' },
  { value: 'metal-foil', label: 'Metal Foil' },
  { value: 'candy-gloss', label: 'Candy Gloss' },
];
// The 2D bevel/sticker Finish (owner 2026-07-20) — matte|satin|glossy|metal,
// anchored to the baked style bundles (fxBevel FINISHES). Distinct from the 3D
// archetype Finish above; the 3D mapping stays a separate control (see RETURN).
const BEVEL_FINISH_OPTIONS = [
  { value: 'matte', label: 'Matte' },
  { value: 'satin', label: 'Satin' },
  { value: 'glossy', label: 'Glossy' },
  { value: 'metal', label: 'Metal' },
];

/** kind 'bevelflyout' — SPLIT button (owner §4): face click = the checkbox
 *  semantics (faceHandler toggles at the engine default, ONE entry,
 *  active-lit); the corner chip opens the tuning panel. Fixed handler keys —
 *  the drawer's verbs verbatim (depth.setBevel*). */
function BevelFlyControl({ d, state, handlers, Icon, drawerSection, onOpenDrawer }) {
  const depth = state.fxBevelDepth ?? 0;
  const width = state.fxBevelWidth ?? 34;
  return (
    <RibbonFlyout
      flyId={d.id}
      label={d.label}
      active={isActive(d.activeWhen, state)}
      onFaceClick={fire(handlers, d.faceHandler)}
      face={<DepthTileFace kind="bevel" className="rbn-depth-face" />}
      panelClassName="rbn-depth-panel"
      panel={({ close }) => (
        <>
          <div className="rbn-fly-slider-row" data-slider-id="bevelDepth">
            <span className="rbn-fly-slider-name">Depth</span>
            <TbSlider
              label={String(depth)}
              value={depth}
              min={0}
              max={250}
              step={1}
              onChange={(v) => fire(handlers, 'depth.setBevelDepth')(v)}
              onCommit={() => handlers?.['history.commitNow']?.()}
            />
          </div>
          <div className="rbn-fly-slider-row" data-slider-id="bevelWidth">
            <span className="rbn-fly-slider-name">Width</span>
            <TbSlider
              label={String(width)}
              value={width}
              min={0}
              max={250}
              step={1}
              onChange={(v) => fire(handlers, 'depth.setBevelWidth')(v)}
              onCommit={() => handlers?.['history.commitNow']?.()}
            />
          </div>
          <div className="rbn-fly-seg-row">
            <span className="rbn-fly-slider-name">Shape</span>
            <TbSegmented
              label="Bevel shape"
              value={state.fxBevelShape ?? 'smooth'}
              options={BEVEL_SHAPE_OPTIONS}
              onChange={(v) => fire(handlers, 'depth.setBevelShape')(v)}
            />
          </div>
          <div className="rbn-fly-seg-row rbn-finish-row">
            <span className="rbn-fly-slider-name">Finish</span>
            <TbSegmented
              label="Bevel finish"
              value={state.fxBevelFinish ?? 'satin'}
              options={BEVEL_FINISH_OPTIONS}
              onChange={(v) => fire(handlers, 'depth.setBevelFinish')(v)}
            />
          </div>
          <FlyCheckRow
            label="Use poster light"
            checked={(state.fxBevelLightMode ?? 'custom') === 'poster'}
            onToggle={(on) => fire(handlers, 'depth.setBevelLight')(on)}
          />
          {/* Layer-separation law (owner 2026-07-20): this dial never domes
              a sticker — its border/badge has its own 'Beveled sticker'. */}
          <div className="rbn-fly-hint">
            Shapes the {state.fxKind && state.fxKind !== 'text' ? 'object' : 'letters'} only
            — a sticker&apos;s border has its own “Beveled sticker” switch.
          </div>
          <FlyoutFooter drawerSection={drawerSection} onOpenDrawer={onOpenDrawer} close={close} />
        </>
      )}
    />
  );
}

/** kind 'threedflyout' — SPLIT button (owner §4): face toggles 3D at the
 *  engine default; the chip opens depth + sticker plate + finish. Sides and
 *  light presets stay drawer-deep ("More options…" footer). */
function ThreeDFlyControl({ d, state, handlers, Icon, drawerSection, onOpenDrawer }) {
  const depth = state.fxExtrudeDepth ?? 0;
  const plate = state.fxExtrudePlate || { enabled: false, width: 50, depth: 40, fill: null };
  return (
    <RibbonFlyout
      flyId={d.id}
      label={d.label}
      active={isActive(d.activeWhen, state)}
      onFaceClick={fire(handlers, d.faceHandler)}
      face={<DepthTileFace kind="extrude" className="rbn-depth-face" />}
      panelClassName="rbn-depth-panel"
      panel={({ close }) => (
        <>
          <div className="rbn-fly-slider-row" data-slider-id="threeDDepth">
            <span className="rbn-fly-slider-name">Depth</span>
            <TbSlider
              label={String(depth)}
              value={depth}
              min={0}
              max={100}
              step={1}
              onChange={(v) => fire(handlers, 'depth.set3DDepth')(v)}
              onCommit={() => handlers?.['history.commitNow']?.()}
            />
          </div>
          {/* Volume style (Depth Everywhere T1) — Solid = classic extrude,
              Puffy = rounded slab, Balloon = inflated pillow. */}
          <div className="rbn-fly-seg-row" data-seg-id="volumeStyle">
            <span className="rbn-fly-slider-name">Style</span>
            <TbSegmented
              label="3D style"
              value={state.fxVolumeStyle ?? 'solid'}
              options={[
                { value: 'solid', label: 'Solid' },
                { value: 'puffy', label: 'Puffy' },
                { value: 'pillow', label: 'Balloon' },
              ]}
              onChange={(v) => fire(handlers, 'depth.setVolumeStyle')(v)}
            />
          </div>
          {(state.fxVolumeStyle === 'puffy' || state.fxVolumeStyle === 'pillow') && (
            <div className="rbn-fly-slider-row" data-slider-id="volumeRound">
              <span className="rbn-fly-slider-name">Roundness</span>
              <TbSlider
                label={String(state.fxVolumeRound ?? 55)}
                value={state.fxVolumeRound ?? 55}
                min={0}
                max={100}
                step={1}
                onChange={(v) => fire(handlers, 'depth.setVolumeRound')(v)}
                onCommit={() => handlers?.['history.commitNow']?.()}
              />
            </div>
          )}
          {state.fxVolumeStyle === 'pillow' && (
            <FlyCheckRow
              label="Balloon back (round both sides)"
              klass="rbn-volback-check"
              checked={state.fxVolumeBack === 'round'}
              onToggle={(on) => fire(handlers, 'depth.setVolumeBack')(on)}
            />
          )}
          <FlyCheckRow
            label="Sticker plate"
            klass="rbn-plate-check"
            checked={!!plate.enabled}
            onToggle={(on) => fire(handlers, 'depth.setPlateOn')(on)}
          />
          {plate.enabled && (
            <>
              <div className="rbn-fly-slider-row" data-slider-id="plateWidth">
                <span className="rbn-fly-slider-name">Plate width</span>
                <TbSlider
                  label={String(plate.width ?? 50)}
                  value={plate.width ?? 50}
                  min={0}
                  max={100}
                  step={1}
                  onChange={(v) => fire(handlers, 'depth.setPlateWidth')(v)}
                  onCommit={() => handlers?.['history.commitNow']?.()}
                />
              </div>
              <div className="rbn-fly-slider-row" data-slider-id="plateDepth">
                <span className="rbn-fly-slider-name">Plate depth</span>
                <TbSlider
                  label={String(plate.depth ?? 40)}
                  value={plate.depth ?? 40}
                  min={0}
                  max={100}
                  step={1}
                  onChange={(v) => fire(handlers, 'depth.setPlateDepth')(v)}
                  onCommit={() => handlers?.['history.commitNow']?.()}
                />
              </div>
              {/* Plate paint — the house PaintField (owner's one-standard
                  law). Engine truth: plate.fill is null (match the letters)
                  | '#hex' | a manifest texture id — so the picker allows
                  solid + texture and the null face reads honestly as the
                  "match" state. depth.setPlateFill sends the WHOLE plate
                  (the patchPlate rule). */}
              <div className="rbn-fx-param-row rbn-plate-fill-row">
                <span className="rbn-fly-slider-name">Plate color</span>
                <PaintField
                  label="Plate color"
                  dataField="plateFill"
                  value={plate.fill == null
                    ? null
                    : (String(plate.fill).startsWith('#') ? plate.fill : { type: 'texture', id: plate.fill })}
                  allow={['solid', 'texture']}
                  opaque
                  allowImport={false}
                  onChange={(next, { live } = {}) => {
                    const v = typeof next === 'string' ? next : next?.type === 'texture' ? next.id : null;
                    if (v != null) fire(handlers, 'depth.setPlateFill')(v, live ? 'defer' : 'now');
                  }}
                  onCommit={() => handlers?.['history.commitNow']?.()}
                />
                <button
                  type="button"
                  className={`rbn-menu-action rbn-plate-match${plate.fill == null ? ' active' : ''}`}
                  title="Match the letters"
                  onClick={() => fire(handlers, 'depth.setPlateFill')(null, 'now')}
                >
                  Match letters
                </button>
              </div>
            </>
          )}
          <div className="rbn-fly-seg-row rbn-finish-row">
            <span className="rbn-fly-slider-name">Finish</span>
            <TbSegmented
              label="3D finish"
              value={state.fxExtrudeFinish ?? 'satin'}
              options={FINISH_OPTIONS}
              onChange={(v) => fire(handlers, 'depth.set3DFinish')(v)}
            />
          </div>
          <FlyoutFooter drawerSection={drawerSection} onOpenDrawer={onOpenDrawer} close={close} />
        </>
      )}
    />
  );
}

/** The live entry for an effect id in the slot mirror, or null. The slot model
 *  keys by SLOT (shadow / stroke); this reads it back by effect ID — the
 *  id-level active read (owner §2). Sticker occupies both slots but reads back
 *  from either. Universal effects (U2): reads the DUAL bag `state.effects`
 *  (data.effects on shapes/icons, data.textEffects on text — the U1 read-compat
 *  alias) so a lit face works for every kind; `textEffects` stays the fallback
 *  for any pre-U1 mirror instance. */
function fxSlotEntry(state, effectId) {
  const fx = state.effects || state.textEffects || {};
  if (fx.shadow?.id === effectId) return fx.shadow;
  if (fx.stroke?.id === effectId) return fx.stroke;
  return null;
}

/** Best solid hex inside a FillValue recipe (the sticker borderColor shape) —
 *  a plain string is already a valid FillValue (effectDefs' contract). */
function fxSolidFromFill(fv) {
  if (!fv) return '#ffffff';
  if (typeof fv === 'string') return fv;
  if (fv.type === 'solid') return fv.color ?? '#ffffff';
  const stops = fv.stops ?? fv.colorStops;
  if (stops?.length) return stops[0].color;
  return '#ffffff';
}

const fxHex = (v, fallback) => (typeof v === 'string' && v.startsWith('#') ? v : fallback);

/** kind 'effectflyout' PANEL — the def's params as compact flyout controls
 *  (owner §3). Built straight from EFFECT_DEFS[id].params, the SAME metadata
 *  the drawer's EffectOptions card reads (no fork). Every change is an
 *  idempotent full re-apply of the merged params through fx.applyParams
 *  (editor.applyTextEffect) — slider ticks defer + commit ONE entry on release,
 *  color/segmented/toggle land at once. Adjusting while the effect is OFF turns
 *  it on at those params (the engine displaces whatever holds the slot),
 *  exactly like the depth dials. A 'fill' param's 'Texture…' row opens the
 *  house explorer in pickMode via the mount (fx.pickFillTexture); the full
 *  thumb grid stays drawer-deep (footer). */
function EffectParamsPanel({ def, entry, state, handlers, drawerSection, onOpenDrawer, close }) {
  const current = { ...effectDefaults(def), ...(entry?.params || {}) };
  const applyParams = (params, mode) => fire(handlers, 'fx.applyParams')(def.id, params, mode);
  const setParam = (key, v, mode) => applyParams({ ...current, [key]: v }, mode);

  // Smart-default display for a null color param (echo inherits the fill,
  // outline auto-contrasts) — mirrors EffectOptions.displayColor.
  const fillSolid = fxSolidFromFill(state.textFill);
  const displayColor = (p) => {
    if (current[p.key] != null) return fxHex(current[p.key], '#000000');
    if (def.id === 'echo') return fxHex(fillSolid, '#000000');
    if (def.id === 'outline') return luminance(fillSolid) > 0.5 ? '#000000' : '#ffffff';
    return fxHex(p.default, '#000000');
  };

  const renderParam = (p) => {
    if (p.type === 'slider' || p.type === 'angle') {
      const v = current[p.key] ?? p.default;
      return (
        <div className="rbn-fly-slider-row" key={p.key} data-slider-id={p.key}>
          <span className="rbn-fly-slider-name">{`${p.label}: ${v}${p.type === 'angle' ? '°' : ''}`}</span>
          <TbSlider
            label={p.label}
            value={Number.isFinite(v) ? v : (p.min ?? 0)}
            min={p.min}
            max={p.max}
            step={p.step}
            onChange={(nv) => setParam(p.key, nv, 'defer')}
            onCommit={() => handlers?.['history.commitNow']?.()}
          />
        </div>
      );
    }
    if (p.type === 'color') {
      // The house PaintField (solid-only — these params are flat colors):
      // chip face shows the live/derived color, popover carries the full
      // modern picker. Live ticks 'soon', discrete picks 'now'.
      return (
        <div className="rbn-fx-param-row" key={p.key}>
          <span className="rbn-fly-slider-name">{p.label}</span>
          <PaintField
            label={`${def.label} ${p.label.toLowerCase()}`}
            dataField={p.key}
            value={displayColor(p)}
            allow={['solid']}
            onChange={(v, { live } = {}) => {
              if (typeof v === 'string') setParam(p.key, v, live ? 'soon' : 'now');
            }}
            onCommit={() => handlers?.['history.commitNow']?.()}
          />
        </div>
      );
    }
    if (p.type === 'segmented') {
      return (
        <div className="rbn-fly-seg-row" key={p.key}>
          <span className="rbn-fly-slider-name">{p.label}</span>
          <TbSegmented
            label={p.label}
            value={current[p.key] ?? p.default}
            options={p.options}
            onChange={(v) => setParam(p.key, v, 'now')}
          />
        </div>
      );
    }
    // 'toggle' (sticker's Beveled sticker): the depth flyouts' checkbox row —
    // boolean param, one history entry per tick.
    if (p.type === 'toggle') {
      return (
        <FlyCheckRow
          key={p.key}
          label={p.label}
          klass={`rbn-fx-toggle-${p.key}`}
          checked={!!current[p.key]}
          onToggle={(on) => setParam(p.key, on, 'now')}
        />
      );
    }
    // 'fill' (sticker borderColor): the FULL house standard in one chip —
    // Solid | Gradient | Texture panes inline (the primitive 'Texture…' row
    // is dead). Solids emit the plain-hex string (the legacy FillValue every
    // saved recipe stores — byte-compatible); gradients emit a FillValue the
    // def's strokePaintFromFillValue builds synchronously; texture picks emit
    // {type:'texture', id, scale} through the SAME applyTextEffect commit
    // path. 'Browse all…' stays the escape hatch into the house explorer via
    // the mount (fx.pickFillTexture) — the flyout closes, the modal opens.
    if (p.type === 'fill') {
      const cur = current[p.key];
      const solid = fxHex(fxSolidFromFill(cur), fxHex(p.default, '#ffffff'));
      const value = cur == null ? solid : cur;
      return (
        <div className="rbn-fx-fill-param" key={p.key} data-fx-fill-param={p.key}>
          <span className="rbn-fly-slider-name">{p.label}</span>
          <PaintField
            label={`${def.label} ${p.label.toLowerCase()}`}
            dataField={p.key}
            value={value}
            allow={['solid', 'gradient', 'texture']}
            onChange={(next, { live } = {}) =>
              setParam(p.key, next, live ? (typeof next === 'string' ? 'soon' : 'defer') : 'now')}
            onCommit={() => handlers?.['history.commitNow']?.()}
            onBrowseAll={() => {
              fire(handlers, 'fx.pickFillTexture')(def.id, p.key);
              close?.();
            }}
          />
        </div>
      );
    }
    return null;
  };

  // showIf(params): a param opts out when meaningless (Outline's gradient stops
  // only exist once the outline IS a gradient) — inert for params without it.
  const visible = def.params.filter((p) => !p.showIf || p.showIf(current));

  return (
    <>
      {def.presets && (
        <div className="rbn-fx-presets">
          {def.presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="rbn-fx-preset"
              onClick={() => applyParams({ ...effectDefaults(def), ...preset.params }, 'now')}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
      {visible.map(renderParam)}
      <FlyoutFooter drawerSection={drawerSection} onOpenDrawer={onOpenDrawer} close={close} />
    </>
  );
}

/** kind 'effectflyout' — SPLIT button (owner §2, the Bevel/3D grammar): face
 *  click = toggle (active → removeTextEffect, else applyTextEffect at defaults,
 *  via fx.toggleEffect(id) — the engine self-batches, one entry); active-lit =
 *  the slot mirror says this id is live. Corner chip opens the def's params. */
function EffectFlyControl({ d, state, handlers, drawerSection, onOpenDrawer }) {
  const def = EFFECT_DEFS[d.effectId];
  if (!def) return null;
  const entry = fxSlotEntry(state, d.effectId);
  // "Trace letters" is text wording — a star has edges, not letters
  const label = d.effectId === 'trace' && state.fxKind && state.fxKind !== 'text'
    ? 'Trace' : d.label;
  return (
    <RibbonFlyout
      flyId={d.id}
      label={label}
      active={!!entry}
      onFaceClick={() => fire(handlers, d.faceHandler)(d.effectId)}
      face={<EffectTileFace effectId={d.effectId} className="rbn-fx-face" />}
      panelClassName="rbn-fx-panel"
      panel={({ close }) => (
        <EffectParamsPanel
          def={def}
          entry={entry}
          state={state}
          handlers={handlers}
          drawerSection={drawerSection}
          onOpenDrawer={onOpenDrawer}
          close={close}
        />
      )}
    />
  );
}

/** kind 'flyoutmenu' — one button, an action list (Arrange). Items are
 *  one-shot verbs: pick fires and the panel closes (navigation closes);
 *  showWhen rows appear exactly when they can act. */
function FlyoutMenuControl({ d, state, handlers, Icon, text, drawerSection, onOpenDrawer, inMenu }) {
  const items = (d.items || []).filter((it) => it.divider || !it.showWhen || !!state[it.showWhen]);
  const rows = (close) => (
    <div className="rbn-fly-opts" role="menu" aria-label={d.label}>
      {items.map((it, i) => {
        if (it.divider) {
          // trailing/leading dividers vanish with their conditional rows
          const before = items[i - 1];
          const after = items[i + 1];
          if (!before || !after || before.divider || after.divider) return null;
          return <div key={`div-${i}`} className="rbn-fly-divider" aria-hidden="true" />;
        }
        const OIcon = getIcon(it.icon);
        return (
          <button
            key={it.id}
            type="button"
            className="rbn-fly-opt"
            role="menuitem"
            title={it.label}
            data-item-id={it.id}
            onClick={() => { fire(handlers, it.handler)(...(it.args || [])); close?.(); }}
          >
            {OIcon ? <OIcon /> : null}
            <span className="rbn-fly-opt-label">{it.label}</span>
          </button>
        );
      })}
    </div>
  );
  if (inMenu) return rows(undefined);
  return (
    <RibbonFlyout
      flyId={d.id}
      label={d.label}
      active={isActive(d.activeWhen, state)}
      face={Icon && text
        ? (<><Icon /><span className="rbn-btn-text">{text}</span></>)
        : Icon ? <Icon /> : <span className="rbn-btn-text">{text ?? d.label}</span>}
      panel={({ close }) => (
        <>
          {rows(close)}
          <FlyoutFooter drawerSection={drawerSection} onOpenDrawer={onOpenDrawer} close={close} />
        </>
      )}
    />
  );
}

export default function RibbonControl({
  descriptor: d, state = {}, handlers = {}, inMenu = false,
  drawerSection = null, onOpenDrawer,
}) {
  const value = d.read != null ? state[d.read] : undefined;
  const val = value ?? d.fallback;
  const on = fire(handlers, d.handler);
  const Icon = getIcon(d.icon);
  // optionsFrom: dynamic option lists ride the mirror bag (font-dependent
  // weight menus etc.) — data stays pure, the mount supplies the list.
  const options = (d.optionsFrom && Array.isArray(state[d.optionsFrom]))
    ? state[d.optionsFrom]
    : (d.options || []);

  // textFrom: live label override (busy states, recipe names) — mirror-fed.
  const text = (d.textFrom && typeof state[d.textFrom] === 'string')
    ? state[d.textFrom]
    : d.text;

  let node = null;
  switch (d.kind) {
    case 'button':
    case 'toggle': {
      // icon, text, or both (Position / Effects… style labeled buttons)
      const inner = Icon && text
        ? (<><Icon /><span className="rbn-btn-text">{text}</span></>)
        : Icon ? <Icon /> : <span className="rbn-btn-text">{text ?? d.label}</span>;
      node = (
        <TbButton
          label={d.label}
          active={isActive(d.activeWhen, state)}
          danger={d.danger}
          onClick={() => on()}
        >
          {inner}
        </TbButton>
      );
      break;
    }
    case 'color':
      node = <TbColorField label={d.label} value={typeof val === 'string' ? val : '#000000'} onChange={(v) => on(v)} />;
      break;
    case 'number':
      node = (
        <TbNumberField
          label={d.label}
          value={Number.isFinite(val) ? val : 0}
          min={d.min}
          max={d.max}
          step={d.step}
          onChange={(v) => on(v)}
        />
      );
      break;
    case 'stepper': {
      const step = d.step ?? 1;
      const cur = Number.isFinite(val) ? val : 0;
      const clamp = (v) => Math.min(d.max ?? Infinity, Math.max(d.min ?? -Infinity, v));
      node = (
        <div className="cce-tb-stepper">
          <button type="button" aria-label={`Decrease ${d.label}`} onClick={() => on(clamp(cur - step))}>−</button>
          <TbNumberField label={d.label} value={cur} min={d.min} max={d.max} step={step} onChange={(v) => on(v)} />
          <button type="button" aria-label={`Increase ${d.label}`} onClick={() => on(clamp(cur + step))}>+</button>
        </div>
      );
      break;
    }
    case 'select':
      node = (
        <select
          className="cce-tb-select"
          value={String(val ?? '')}
          aria-label={d.label}
          title={d.label}
          onChange={(e) => on(e.target.value)}
        >
          {options.map((o) => (
            <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
          ))}
        </select>
      );
      break;
    case 'segmented':
      node = <TbSegmented label={d.label} value={val} options={options} onChange={(v) => on(v)} />;
      break;
    case 'chips':
      node = (
        <div className="rbn-chips" role="group" aria-label={d.label}>
          {options.map((o) => {
            const OIcon = getIcon(o.icon);
            return (
              <TbButton
                key={String(o.value)}
                icon={OIcon ? <OIcon /> : undefined}
                label={o.label}
                active={val === o.value}
                onClick={() => on(o.value)}
              >
                {!OIcon && o.text ? <span className="rbn-btn-text">{o.text}</span> : null}
              </TbButton>
            );
          })}
        </div>
      );
      break;
    case 'slider':
      // Sliders end their drag through ONE well-known key so every drag lands
      // as a single undo entry (house commit rule). Optional in R0 mocks;
      // R1 wires 'history.commitNow' to useSelectionProps' commitNow.
      node = (
        <div className={`rbn-slider${inMenu ? '' : ' inline'}`}>
          <TbSlider
            label={typeof d.format === 'function' ? d.format(val) : (d.label ?? '')}
            value={Number.isFinite(val) ? val : (d.min ?? 0)}
            min={d.min ?? 0}
            max={d.max ?? 100}
            step={d.step ?? 1}
            onChange={(v) => on(v)}
            onCommit={() => handlers?.['history.commitNow']?.()}
          />
        </div>
      );
      break;
    case 'font':
      // The house font control (searchable catalog, previews, recently-used)
      // behind the same handler key the R0 select reserved.
      node = (
        <FontPicker
          value={typeof val === 'string' ? val : 'Arial'}
          onSelect={(family) => on(family)}
        />
      );
      break;
    case 'badge': {
      // Read-only status pill. Mirror gives a string, or {text, tone, title}
      // (tone: 'good'|'warn'|'bad' — the DPI traffic light).
      const b = typeof val === 'string' ? { text: val } : (val || null);
      node = b ? (
        <span
          className="rbn-badge"
          data-tone={b.tone || 'neutral'}
          title={b.title || d.label}
        >
          {b.text}
        </span>
      ) : null;
      break;
    }
    case 'sizefield':
      // collapsed-menu fallback: a plain number field (no popover-in-popover)
      node = inMenu
        ? (
          <TbNumberField
            label={d.label}
            value={Number.isFinite(val) ? Math.round(val) : 0}
            min={d.min}
            max={d.max}
            step={d.step}
            onChange={(v) => on(v)}
          />
        )
        : <SizeFieldControl d={d} state={state} handlers={handlers} on={on} />;
      break;
    case 'flyoutchips': {
      const cur = val ?? d.fallback;
      const isCur = (o) => String(o.value) === String(cur);
      if (inMenu) {
        // collapsed-menu fallback: the flat chips row (R2 rendering)
        node = (
          <div className="rbn-chips" role="group" aria-label={d.label}>
            {options.map((o) => {
              const OIcon = getIcon(o.icon);
              return (
                <TbButton key={String(o.value)} icon={OIcon ? <OIcon /> : undefined} label={o.label} active={isCur(o)} onClick={() => on(o.value)}>
                  {!OIcon && (o.text || o.label) ? <span className="rbn-btn-text">{o.text ?? o.label}</span> : null}
                </TbButton>
              );
            })}
          </div>
        );
        break;
      }
      // Face mirrors the CURRENT option — except at the fallback value the
      // control shows its IDENTITY icon (a quiet bullet reads "lists live
      // here"; the 'none' row's slash icon would read "forbidden").
      const curOpt = options.find(isCur);
      const mirrorFace = d.faceFromOptions && curOpt && String(curOpt.value) !== String(d.fallback);
      const FaceIcon = (mirrorFace && curOpt.icon ? getIcon(curOpt.icon) : null) || Icon;
      const faceText = (mirrorFace && curOpt.text) || text;
      const face = FaceIcon
        ? <FaceIcon />
        : <span className="rbn-btn-text">{faceText ?? d.label}</span>;
      node = (
        <RibbonFlyout
          flyId={d.id}
          label={d.label}
          active={isActive(d.activeWhen, state)}
          onFaceClick={d.faceHandler ? fire(handlers, d.faceHandler) : undefined}
          face={face}
          panel={({ close }) => (
            <>
              <div className="rbn-fly-opts" role="menu" aria-label={d.label}>
                {options.map((o) => {
                  const OIcon = getIcon(o.icon);
                  return (
                    <FlyoutOption
                      key={String(o.value)}
                      value={o.value}
                      label={o.label}
                      sample={o.text ?? null}
                      icon={OIcon ? <OIcon /> : null}
                      current={isCur(o)}
                      onPick={() => { on(o.value); close(); }}
                    />
                  );
                })}
              </div>
              <FlyoutFooter drawerSection={drawerSection} onOpenDrawer={onOpenDrawer} close={close} />
            </>
          )}
        />
      );
      break;
    }
    case 'colorflyout': {
      const solid = typeof val === 'string' ? val : '#111827';
      const recipe = d.recipeRead ? state[d.recipeRead] : state.textFill;
      const solidOnly = d.solidOnlyWhen ? !!state[d.solidOnlyWhen] : false;
      // d.allow (['solid','gradient']…) narrows the panes to the host's
      // engine truth (page background); d.paletteFrom folds a mirror-fed
      // swatch row (the old presets strip) into the Solid palette.
      const palette = (d.paletteFrom && Array.isArray(state[d.paletteFrom]))
        ? state[d.paletteFrom]
        : undefined;
      if (inMenu) {
        node = <TbColorField label={d.label} value={solid.startsWith('#') ? solid : '#111827'} onChange={(v) => on(v, 'soon')} />;
        break;
      }
      // Owner 2026-07-21: the chip opens the ONE house paint modal DIRECTLY —
      // no flyout menu (the ColorFlyoutPanel-in-menu hosting is gone). The
      // modal carries the SAME PaintPicker grammar the flyout did: solids ride
      // d.handler ('soon' live / 'now' discrete), recipes ride d.recipeHandler
      // ('defer' live / 'now'); Apply lands ONE entry via history.commitNow.
      // Title is kind-true (Fill / Icon color / Text color, mount-fed).
      const colorTitle = state.colorTitle || d.label;
      const allowPanes = solidOnly ? ['solid'] : (d.allow ?? ['solid', 'gradient', 'texture']);
      const chipValue = recipe && typeof recipe === 'object' ? recipe : solid;
      node = (
        <PaintChip
          value={chipValue}
          className="rbn-swatch-face rbn-color-chip"
          title={colorTitle}
          ariaHaspopup="dialog"
          onClick={() => openPaintModal({
            fieldId: d.id,
            title: colorTitle,
            value: chipValue,
            allow: allowPanes,
            linearOnly: !!d.linearOnly,
            palette,
            density: 'compact',
            onChange: (next, { live } = {}) => {
              if (typeof next === 'string') fire(handlers, d.handler)(next, live ? 'soon' : 'now');
              else fire(handlers, d.recipeHandler)(next, live ? 'defer' : 'now');
            },
            onCommit: () => handlers?.['history.commitNow']?.(),
            onBrowseAll: (!solidOnly && allowPanes.includes('texture'))
              ? () => handlers?.['ui.openTextureExplorer']?.()
              : undefined,
          })}
        />
      );
      break;
    }
    case 'outlineflyout':
      // collapsed-menu fallback: the flat R1 pair (color field + width box)
      node = inMenu
        ? (
          <span className="rbn-outline-flat">
            <TbColorField
              label={`${d.label} color`}
              value={typeof state[d.colorRead] === 'string' ? state[d.colorRead] : '#000000'}
              onChange={(v) => fire(handlers, d.colorHandler)(v)}
            />
            <TbNumberField
              label={`${d.label} width`}
              value={Number.isFinite(val) ? val : 0}
              min={d.min}
              max={d.max}
              step={d.step}
              onChange={(v) => on(v)}
            />
          </span>
        )
        : (
          <OutlineFlyControl
            d={d}
            state={state}
            handlers={handlers}
            Icon={Icon}
            drawerSection={drawerSection}
            onOpenDrawer={onOpenDrawer}
          />
        );
      break;
    case 'bevelflyout':
    case 'threedflyout': {
      // collapsed-menu fallback: the face toggle alone (tuning stays one
      // level up — no popover-in-popover)
      if (inMenu) {
        node = (
          <TbButton label={d.label} active={isActive(d.activeWhen, state)} onClick={() => fire(handlers, d.faceHandler)()}>
            <DepthTileFace kind={d.kind === 'bevelflyout' ? 'bevel' : 'extrude'} className="rbn-depth-face" />
          </TbButton>
        );
        break;
      }
      const DepthCmp = d.kind === 'bevelflyout' ? BevelFlyControl : ThreeDFlyControl;
      node = (
        <DepthCmp
          d={d}
          state={state}
          handlers={handlers}
          Icon={Icon}
          drawerSection={drawerSection}
          onOpenDrawer={onOpenDrawer}
        />
      );
      break;
    }
    case 'effectflyout': {
      // collapsed-menu fallback: the face toggle alone (params stay one level
      // up — no popover-in-popover, the established degrade rule)
      if (inMenu) {
        node = (
          <TbButton
            label={d.label}
            active={!!fxSlotEntry(state, d.effectId)}
            onClick={() => fire(handlers, d.faceHandler)(d.effectId)}
          >
            <EffectTileFace effectId={d.effectId} className="rbn-fx-face" />
          </TbButton>
        );
        break;
      }
      node = (
        <EffectFlyControl
          d={d}
          state={state}
          handlers={handlers}
          drawerSection={drawerSection}
          onOpenDrawer={onOpenDrawer}
        />
      );
      break;
    }
    case 'flyoutmenu':
      node = (
        <FlyoutMenuControl
          d={d}
          state={state}
          handlers={handlers}
          Icon={Icon}
          text={text}
          drawerSection={drawerSection}
          onOpenDrawer={onOpenDrawer}
          inMenu={inMenu}
        />
      );
      break;
    case 'flyoutsliders': {
      const rows = (close) => (
        <div className="rbn-fly-sliders">
          {(d.sliders || []).map((s) => {
            const sv = state[s.read];
            const cur = Number.isFinite(sv) ? sv : (s.fallback ?? s.min ?? 0);
            return (
              <div className="rbn-fly-slider-row" key={s.id} data-slider-id={s.id}>
                <span className="rbn-fly-slider-name">{s.label}</span>
                <TbSlider
                  label={typeof s.format === 'function' ? s.format(cur) : String(cur)}
                  value={cur}
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  onChange={(v) => fire(handlers, s.handler)(v)}
                  onCommit={() => handlers?.['history.commitNow']?.()}
                />
              </div>
            );
          })}
        </div>
      );
      if (inMenu) { node = rows(undefined); break; }
      node = (
        <RibbonFlyout
          flyId={d.id}
          label={d.label}
          active={isActive(d.activeWhen, state)}
          face={Icon ? <Icon /> : <span className="rbn-btn-text">{text ?? d.label}</span>}
          panelClassName="rbn-sliders-panel"
          panel={({ close }) => (
            <>
              {rows(close)}
              <FlyoutFooter drawerSection={drawerSection} onOpenDrawer={onOpenDrawer} close={close} />
            </>
          )}
        />
      );
      break;
    }
    case 'placeholder':
      node = (
        <span className="rbn-placeholder" title={`${d.label} — arrives in a later phase`}>
          {(d.read != null && typeof state[d.read] === 'string' ? state[d.read] : null) || text || d.label}
        </span>
      );
      break;
    default:
      node = <span className="rbn-placeholder" title={`Unknown control kind: ${d.kind}`}>{d.kind}?</span>;
  }

  // Per-control disabled gate (R3): its own <fieldset disabled> — native
  // cascade turns the control off without touching siblings; the tooltip
  // says WHY in plain words (state never hides, lies never render live).
  const offNow = d.disabledWhen ? !!state[d.disabledWhen] : false;
  // disabledTitleFrom (mirror key, mirrors textFrom): a live disabled tooltip
  // when one control can be off for more than one reason — e.g. an effect that
  // is text-only AND 3D-gated (Hollow/Trace/Sticker) needs the reason that
  // actually applies. Falls back to the static disabledTitle.
  const offTitle = (d.disabledTitleFrom && typeof state[d.disabledTitleFrom] === 'string')
    ? state[d.disabledTitleFrom]
    : (d.disabledTitle || d.label);

  return (
    <span
      className={`rbn-control${inMenu ? ' rbn-control-menu' : ''}`}
      data-control-id={d.id}
      data-handler-key={d.handler || ''}
      data-commit={d.commit || 'now'}
      data-ctl-disabled={offNow ? 'true' : 'false'}
    >
      {inMenu && d.kind !== 'placeholder' ? <span className="rbn-menu-item-label">{d.label}</span> : null}
      {offNow
        ? (
          <fieldset className="rbn-ctl-off" disabled title={offTitle}>
            {node}
          </fieldset>
        )
        : node}
    </span>
  );
}
